import React, { useMemo } from 'react';
import { Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useTimeStore } from '@/store/timeStore';
import { useProjects } from '@/hooks/useProjects';
import { formatHoursMinutes } from '@/utils/timeUtils';

interface TodayShiftsSummaryProps {
  userId?: string;
}

export const TodayShiftsSummary: React.FC<TodayShiftsSummaryProps> = ({ userId }) => {
  // @ts-expect-error - timeEntries est ajouté dynamiquement dans le store
  const { timeEntries } = useTimeStore();
  const { projects } = useProjects();

  // Filtrer les entrées d'aujourd'hui et les grouper par projet
  const todayShiftsByProject = useMemo(() => {
    if (!timeEntries.length || !projects.length) return [];

    const today = new Date().toDateString();
    const todayEntries = timeEntries.filter(
      (entry: any) => new Date(entry.start_time).toDateString() === today
    );

    // Grouper par projet
    const projectMap = new Map<string, { projectName: string; totalMinutes: number }>();

    todayEntries.forEach((entry: any) => {
      const project = projects.find((p) => p.id === entry.project_id);
      if (!project) return;

      const existing = projectMap.get(entry.project_id);
      const minutes = entry.duration_minutes || 0;

      if (existing) {
        existing.totalMinutes += minutes;
      } else {
        projectMap.set(entry.project_id, {
          projectName: project.name,
          totalMinutes: minutes,
        });
      }
    });

    return Array.from(projectMap.values()).sort((a, b) => b.totalMinutes - a.totalMinutes);
  }, [timeEntries, projects]);

  if (todayShiftsByProject.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Aujourd'hui
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-1">
          {todayShiftsByProject.map((shift, index) => (
            <div
              key={index}
              className="flex items-center justify-between text-sm py-1"
            >
              <span className="text-muted-foreground truncate max-w-[60%]">
                {shift.projectName}
              </span>
              <span className="font-medium text-blue-600">
                {formatHoursMinutes(shift.totalMinutes)}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
